import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { Lbl, Inp, Btn, Err, Ok } from './Layout'

// Generalized 2026-08-25 from the admin-only 24h gate to every role, every
// 30 days — a brand-new signup has never verified, so this is due
// immediately on first login too, which is what makes it double as the
// "no registration without OTP" requirement without a separate flow.
const VERIFY_EVERY_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

// FIX: this used to be a pure localStorage check — `admin-otp-verified-at:
// <email>` set on success, read back to decide if the gate should show.
// That's spoofable: anyone with a stolen session token could just set that
// key in their own browser and skip the code entirely, since nothing
// server-side ever confirmed it. Now reads last_verified_at from the
// admin_otp table itself (RLS-scoped to the caller's own row) — the
// gate's decision is driven by the database, not the browser's word for it.
// DISABLED 2026-08-31, at user's request — "no OTP required anywhere to
// login." Always reports "not due" so App.js never shows the gate for
// anyone, without touching App.js itself or any of the OTP-sending/
// verifying backend (api/admin-otp.js, the admin_otp table) — all of that
// stays intact and this one-line change is all that's needed to turn the
// gate back on later.
export async function checkOtpDue(email) {
  return false
}

export default function OtpGate({ profile, onVerified }) {
  const [code,     setCode]     = useState('')
  const [channel,  setChannel]  = useState('email')
  const [busy,     setBusy]     = useState(false)
  const [err,      setErr]      = useState('')
  const [ok,       setOk]       = useState('')
  const [sentOnce, setSentOnce] = useState(false)

  const sendCode = useCallback(async (useChannel) => {
    setBusy(true); setErr(''); setOk('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/admin-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'send', accessToken: session?.access_token, channel: useChannel }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Could not send code')
      if (json.channel === 'whatsapp') setOk(`Code sent to your WhatsApp`)
      else if (json.whatsappRequested)  setOk(`WhatsApp isn't set up yet — sent to ${profile.email} instead`)
      else                              setOk(json.emailSent ? `Code sent to ${profile.email}` : 'Code generated (email not configured — check with support)')
      setSentOnce(true)
    } catch (e) {
      setErr(e.message)
    }
    setBusy(false)
  }, [profile.email])

  useEffect(() => { sendCode(channel) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function verify(e) {
    e.preventDefault()
    setBusy(true); setErr(''); setOk('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/admin-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify', accessToken: session?.access_token, code }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Verification failed')
      onVerified()
    } catch (e) {
      setErr(e.message)
    }
    setBusy(false)
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(5,8,14,0.88)', backdropFilter:'blur(6px)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <form onSubmit={verify} style={{ width:'100%', maxWidth:'380px', background:'var(--bg-page)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'18px', padding:'28px 26px', boxShadow:'0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ fontSize:'28px', marginBottom:'6px' }}>🔐</div>
        <div style={{ fontSize:'17px', fontWeight:800, color:'rgba(var(--fg-rgb),var(--a90))' }}>Verify it's you</div>
        <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'4px', lineHeight:1.5 }}>
          Enter the 6-digit code sent to <strong style={{ color:'rgba(var(--fg-rgb),var(--a60))' }}>{profile.email}</strong>.
        </div>

        {profile.phone && (
          <div style={{ display:'flex', gap:'6px', marginTop:'14px' }}>
            {[{ v:'email', label:'📧 Email' }, { v:'whatsapp', label:'💬 WhatsApp' }].map(o => (
              <button key={o.v} type="button" disabled={busy}
                onClick={() => { setChannel(o.v); sendCode(o.v) }}
                style={{ flex:1, fontSize:'11px', fontWeight:700, padding:'7px 10px', borderRadius:'8px', border:`1px solid ${channel===o.v?'#1e90ff':'rgba(var(--fg-rgb),0.1)'}`, background:channel===o.v?'rgba(30,144,255,0.1)':'transparent', color:channel===o.v?'#5aabff':'rgba(var(--fg-rgb),var(--a45))', cursor:'pointer', fontFamily:'inherit' }}>
                {o.label}
              </button>
            ))}
          </div>
        )}

        <Lbl>Verification Code</Lbl>
        <Inp
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456"
          inputMode="numeric"
          autoFocus
          style={{ fontSize:'22px', letterSpacing:'8px', textAlign:'center', fontWeight:700 }}
        />

        <Err msg={err} />
        <Ok msg={ok} />

        <Btn type="submit" busy={busy} disabled={code.length !== 6}>Verify</Btn>

        <button
          type="button"
          onClick={() => sendCode(channel)}
          disabled={busy}
          style={{ width:'100%', marginTop:'10px', padding:'8px', background:'transparent', border:'none', color:'rgba(var(--fg-rgb),var(--a35))', fontSize:'12px', cursor:'pointer', fontFamily:'inherit' }}
        >
          {sentOnce ? "Didn't get it? Resend code" : 'Send code'}
        </button>
      </form>
    </div>
  )
}
